import React, { useState, useEffect } from 'react';
import { getRequiredCredentialsForExchange } from './exchange/getRequiredCredentials';
import { createConnectionEntity, createWalletAddress, storeApiKey } from './api/connections';
import { getAllExchanges } from './api/exchanges';
import { useExchangeStore } from './stores/exchangeStore';
import { usePortfolioStore } from './stores/portfolioStore';
import { getNativeAssetIdForChain } from './features/onchain/storeOnchainAsset';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const AddConnectionSection = ({ onConnectionAdded, userId }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('exchange');
  const [exchange, setExchange] = useState('');
  const [exchangeOptions, setExchangeOptions] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [password, setPassword] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletChain, setWalletChain] = useState('ethereum');
  const [walletLabel, setWalletLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const setConnected = useExchangeStore(s => s.setConnected);
  const addOnchainAsset = usePortfolioStore(s => s.addOnchainAsset);

  useEffect(() => {
    if (showModal && modalType === 'exchange') {
      getAllExchanges()
        .then((exchanges) => {
          // exchanges: [{code, source}]
          setExchangeOptions(exchanges);
        })
        .catch(() => setExchangeOptions([]));
    }
  }, [showModal, modalType]);

  const handleAddExchange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // 1. Créer l'entité principale (si elle n'existe pas déjà)
      const entity = await createConnectionEntity({
        userId,
        type: 'EXCHANGE',
        name: exchange,
        status: 'ACTIVE'
      });
      // 2. Stocker les credentials via l'API sécurisée (chiffrement côté backend)
      const payload = { userId, exchange };
      const required = getRequiredCredentialsForExchange(exchange);
      if (required.includes('apiKey')) payload.apiKey = apiKey;
      if (required.includes('secret')) payload.apiSecret = apiSecret;
      if (required.includes('passphrase')) payload.passphrase = passphrase;
      if (required.includes('password')) payload.password = password;
      await storeApiKey(payload);
      setConnected(entity.id); // Utilise l'ID d'entité pour Zustand
      setShowModal(false);
      setApiKey('');
      setApiSecret('');
      setPassphrase('');
      setPassword('');
      if (onConnectionAdded) onConnectionAdded();
    } catch (err) {
      setError(err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWallet = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const entity = await createConnectionEntity({
        userId,
        type: 'WALLET',
        name: walletChain,
        status: 'ACTIVE'
      });
      await createWalletAddress({
        connectionEntity: { id: entity.id },
        address: walletAddress,
        chain: walletChain,
        label: walletLabel
      });
      addOnchainAsset({
        id: walletAddress + '-' + walletChain,
        assetId: getNativeAssetIdForChain(walletChain),
        quantity: 0,
        sourceType: 'ONCHAIN',
        address: walletAddress,
        chain: walletChain,
        blockchain: walletChain,
        label: walletLabel
      });
      setShowModal(false);
      setWalletAddress('');
      setWalletLabel('');
      if (onConnectionAdded) onConnectionAdded();
    } catch (err) {
      setError(err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-connection-section">
      <div className="section-title">Ajouter une connexion</div>
      <button onClick={() => { setShowModal(true); setModalType('exchange'); }}>Ajouter un Exchange</button>
      <button onClick={() => { setShowModal(true); setModalType('wallet'); }}>Ajouter un Wallet</button>
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal">
            {modalType === 'exchange' ? (
              <form onSubmit={handleAddExchange}>
                <h3>Connecter un Exchange</h3>
                <label>Exchange
                  <Autocomplete
                    options={exchangeOptions}
                    getOptionLabel={option => option.code}
                    value={exchangeOptions.find(e => e.code === exchange) || null}
                    onChange={(_, newValue) => setExchange(newValue ? newValue.code : '')}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option.code}
                        <span style={{marginLeft:8, color:'#888', fontSize:12}}>
                          {option.source === 'connector' ? ' (native)' : ' (ccxt)'}
                        </span>
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Exchange" required />}
                    fullWidth
                    autoHighlight
                  />
                </label>
                {(() => {
                  const required = getRequiredCredentialsForExchange(exchange);
                  return (
                    <>
                      {required.includes('apiKey') && (
                        <label>Clé API
                          <input value={apiKey} onChange={e => setApiKey(e.target.value)} required />
                        </label>
                      )}
                      {required.includes('secret') && (
                        <label>Secret API
                          <input value={apiSecret} onChange={e => setApiSecret(e.target.value)} required />
                        </label>
                      )}
                      {required.includes('passphrase') && (
                        <label>Passphrase API
                          <input value={passphrase} onChange={e => setPassphrase(e.target.value)} required />
                        </label>
                      )}
                      {required.includes('password') && (
                        <label>Mot de passe API
                          <input value={password} onChange={e => setPassword(e.target.value)} required />
                        </label>
                      )}
                    </>
                  );
                })()}
                {error && <div className="error">{error}</div>}
                <button type="submit" disabled={loading}>{loading ? 'Connexion...' : 'Connecter'}</button>
                <button type="button" onClick={() => setShowModal(false)} disabled={loading}>Annuler</button>
              </form>
            ) : (
              <form onSubmit={handleAddWallet}>
                <h3>Ajouter un Wallet on-chain</h3>
                <label>Label
                  <input value={walletLabel} onChange={e => setWalletLabel(e.target.value)} placeholder="ex: Wallet Binance" />
                </label>
                <label>Adresse du wallet
                  <input value={walletAddress} onChange={e => setWalletAddress(e.target.value)} required />
                </label>
                <label>Blockchain
                  <select value={walletChain} onChange={e => setWalletChain(e.target.value)}>
                    <option value="ethereum">Ethereum</option>
                    <option value="polygon">Polygon</option>
                    <option value="bsc">Binance Smart Chain</option>
                    <option value="akash">Akash</option>
                    <option value="fet">Fetch.ai / ASI</option>
                    <option value="bitcoin">Bitcoin</option>
                    <option value="solana">Solana</option>
                    <option value="sui">Sui</option>
                    <option value="tao">TAO / Bittensor</option>
                  </select>
                </label>
                {error && <div className="error">{error}</div>}
                <button type="submit" disabled={loading}>{loading ? 'Ajout...' : 'Ajouter'}</button>
                <button type="button" onClick={() => setShowModal(false)} disabled={loading}>Annuler</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddConnectionSection;
